import {ChangeDetectorRef, Component, NgZone} from '@angular/core';
import BusinessEntity, {Address, BusinessCategory, BusinessType} from 'src/app/services/businessEntity.model';
import FarmSiteModel from '../../../../../services/farmSite.model';
import PersonsModel, {addUserToAclObj, IAddress, sendPasswordToMobileEmailObj} from '../../../../../services/persons.model';
import BusinessTypeModel from '../../../../../services/businessType.model';
import BusinessCategoryModel from '../../../../../services/businessCategory.model';
import CustomerLeadModel from '../../../../../services/customerLead.model';
import PersonRoleModel from '../../../../../services/personRole.model';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {StateModel, VillageModel} from '../../../../../services/json.model';
import {BusinessEntityService} from '../../../../../services/businessEntity.service';
import {FarmSiteService} from '../../../../../services/farmSite.service';
import {PersonsService} from '../../../../../services/persons.service';
import {CustomerLeadService} from '../../../../../services/customerLead.service';
import {BusinessTypeService} from '../../../../../services/businessType.service';
import {BusinessCategoryService} from '../../../../../services/businessCategory.service';
import {PersonRoleService} from '../../../../../services/personRole.service';
import {ActivatedRoute, Router} from '@angular/router';
import {Sidenavaction} from '../../../../../../assets/js/application/sidenavaction';
import * as _ from 'lodash';
import * as moment from 'moment';
import {ToastrService} from 'ngx-toastr';
import { CommonMethodsService } from 'src/app/services/commonMethods.service';
import { LocationStrategy } from '@angular/common';
import { PreviousRouteService } from 'src/app/services/previousRouteService';
import { StatesAPIService } from 'src/app/services/states.service';
import { CountriesAPIService } from 'src/app/services/countries.service';
import CountriesModel from 'src/app/services/countries.model';

declare var $: any;

/**
 * QuickRegistrationComponent
 *
 * This component handles the quick registration process for new business entities (Farms or Feed Providers)
 * within the Trading Community module of the Admin Panel. It streamlines the onboarding process by
 * combining business entity creation, farm site setup, and user registration into a single flow.
 *
 * Key Responsibilities:
 * - Manage the registration form for new businesses and users.
 * - Handle dynamic loading of countries, states, and villages for address selection.
 * - Validate user input including business details, contact information, and location.
 * - Create a customer lead, business entity, farm site, and user account in sequence.
 * - Generate and send login credentials to the new user via email/SMS.
 * - Handle role-based logic for different business types (Farm vs Feed Provider).
 *
 * Features:
 * - **Multi-step Registration**: Sequentially creates Lead -> Business -> Farm Site -> Person.
 * - **Dynamic Address Selection**: Loads states and villages based on country selection with infinite scroll support for villages.
 * - **Form Validation**: Comprehensive validation for all input fields including regex patterns for names and mobile numbers.
 * - **Business Code Generation**: Automatically suggests business codes based on the business name.
 * - **User Verification**: Adds the new user to the Access Control List (ACL) and triggers password generation.
 * - **Error Handling**: Rollback mechanism to delete created entities if any step in the registration fails.
 *
 * Data Flow:
 * 1. Component initializes (`ngOnInit`) and loads reference data (Countries, Business Types, etc.).
 * 2. User fills out the registration form.
 * 3. `Registration()` creates a Customer Lead.
 * 4. `addBusiness()` creates the Business Entity linked to the lead.
 * 5. `addFarmsite()` creates a Farm Site (Location) for the business.
 * 6. `addPerson()` creates a User account linked to the business and location.
 * 7. `personVerification()` adds the user to ACL and sends credentials.
 *
 * @component
 * @selector app-quick-registration
 * @example
 * <app-quick-registration></app-quick-registration>
 *
 * @implements {OnInit}
 * @implements {OnDestroy}
 * @author Generated by CompDoc
 * @version 1.0.0
 */
@Component({
  selector: 'app-quick-registration',
  templateUrl: './quick-registration.component.html',
  styleUrls: ['./quick-registration.component.scss']
})
export class QuickRegistrationComponent {
  /**
   * Array of business entities available in the system.
   * @type {Array<BusinessEntity>}
   */
  public businessEntities: Array<BusinessEntity>;

  /**
   * Model for the business entity being registered.
   * @type {BusinessEntity}
   */
  public businessEntity: BusinessEntity;

  /**
   * Array of farm sites (locations).
   * @type {Array<FarmSiteModel>}
   */
  public farmsites: Array<FarmSiteModel>;

  /**
   * Model for the farm site being created.
   * @type {FarmSiteModel}
   */
  public farmsite: FarmSiteModel;

  /**
   * Array of persons (users).
   * @type {Array<PersonsModel>}
   */
  public persons: Array<PersonsModel>;

  /**
   * Model for the person (user) being registered.
   * @type {PersonsModel}
   */
  public person: PersonsModel;

  /**
   * Array of customer leads.
   * @type {Array<CustomerLeadModel>}
   */
  public customerLeads: Array<CustomerLeadModel>;

  /**
   * Model for the customer lead being created.
   * @type {CustomerLeadModel}
   */
  public customerLead: CustomerLeadModel;

  /**
   * Array of available business types.
   * @type {Array<BusinessTypeModel>}
   */
  public businessTypes: Array<BusinessTypeModel>;

  /**
   * Selected business type model.
   * @type {BusinessType}
   */
  public businessType: BusinessType;

  /**
   * Array of available business categories.
   * @type {Array<BusinessCategoryModel>}
   */
  public businessCategories: Array<BusinessCategoryModel>;

  /**
   * Selected business category model.
   * @type {BusinessCategory}
   */
  public businessCategory: BusinessCategory;

  /**
   * Array of available person roles.
   * @type {Array<PersonRoleModel>}
   */
  public personRoles: Array<PersonRoleModel>;

  /**
   * Selected person role model.
   * @type {PersonRoleModel}
   */
  public personRole: PersonRoleModel;

  /**
   * Address model for the business entity.
   * @type {Address}
   */
  public address: Address;

  /**
   * Address model for the farm site.
   * @type {IAddress}
   */
  public farmSiteAddress: IAddress;

  /**
   * Reactive form group for the registration form.
   * @type {FormGroup}
   */
  public registerForm: FormGroup;

  /**
   * Object containing data to add user to ACL.
   * @type {addUserToAclObj}
   */
  public addUserToAcl: addUserToAclObj;

  /**
   * Object containing data to send password to user.
   * @type {sendPasswordToMobileEmailObj}
   */
  public sendPwdTouser: sendPasswordToMobileEmailObj;

  /**
   * Flag indicating if the form has been submitted.
   * @type {boolean}
   * @default false
   */
  public submitted = false;

  /**
   * Generic item property.
   * @type {any}
   */
  public item: any;

  /**
   * Selected state item.
   * @type {any}
   */
  public stateitem: any;

  /**
   * Selected village item.
   * @type {any}
   */
  public villageItem: any;

  /**
   * Keyword for filtering.
   * @type {string}
   */
  public keyword: string;

  /**
   * Keyword for filtering states.
   * @type {string}
   */
  public statekeyword: string;

  /**
   * Keyword for filtering villages.
   * @type {string}
   */
  public villagekeyword: string;

  /**
   * Type of person being registered (e.g., 'Business Admin').
   * @type {string}
   */
  public personType: string;

  /**
   * Array of available countries.
   * @type {Array<any>}
   */
  public countries = new Array();

  /**
   * Array of available states for the selected country.
   * @type {Array<StateModel>}
   */
  public states: Array<StateModel>;

  /**
   * Array of available villages for the selected state.
   * @type {Array<VillageModel>}
   */
  public villages: Array<VillageModel>;

  /**
   * Generated password object.
   * @type {any}
   */
  public passowrdGenerated: any;

  /**
   * Response from adding user to ACL.
   * @type {any}
   */
  public addUsertoacl: any;

  /**
   * Response from sending password to user.
   * @type {string}
   */
  public passowrdSent: string;

  /**
   * Dialing code for the selected country.
   * @type {String}
   */
  public dialingCode: String;

  /**
   * Flag indicating if the registration process is in progress.
   * @type {boolean}
   * @default false
   */
  public registraionSubmit: boolean = false;

  /**
   * Flag to disable state and village dropdowns.
   * @type {boolean}
   * @default false
   */
  public stateVillageDisabled: boolean = false;

  /**
   * Flag to control country dropdown visibility.
   * @type {boolean}
   * @default false
   */
  public isCountryDropdown = false;

  /**
   * Flag to control state dropdown visibility.
   * @type {boolean}
   * @default false
   */
  public isStateDropdown = false;

  /**
   * Flag to control village dropdown visibility.
   * @type {boolean}
   * @default false
   */
  public isVillageDropdown = false;

  /**
   * Filtered list of countries for dropdown.
   * @type {any[]}
   */
  public filteredCountriesList: any[] = [];

  /**
   * Filtered list of states for dropdown.
   * @type {any[]}
   */
  public filteredStatesList: any[] = [];

  /**
   * Filtered list of villages for dropdown.
   * @type {any[]}
   */
  public filteredVillagesList: any[] = [];

  /**
   * Limited list of villages for display performance.
   * @type {any[]}
   */
  public limitedVillagesList: any[] = [];

  /**
   * Placeholder text for country search input.
   * @type {string}
   * @default 'Enter Country name'
   */
  public countrySearch: string = 'Enter Country name';

  /**
   * Placeholder text for state search input.
   * @type {string}
   * @default 'Enter State name'
   */
  public stateSearch: string = 'Enter State name';

  /**
   * Placeholder text for village search input.
   * @type {string}
   * @default 'Enter Village name'
   */
  public villageSearch: string = 'Enter Village name';

  /**
   * Name of the selected business type.
   * @type {string}
   */
  public businessTypeName: string;

  /**
   * ID of the created business entity.
   * @type {string}
   */
  public businessID: string;

  /**
   * Flag to disable the submit button.
   * @type {boolean}
   */
  public submitButtonDisabled: boolean;

  /**
   * Processed farm name (formatted).
   * @type {string}
   */
  public finalFarmName: string;

  /**
   * Constructor for QuickRegistrationComponent
   *
   * Initializes the component, injects required services, and sets up the reactive form.
   *
   * @constructor
   * @param {ToastrService} toasterService - Service for displaying toast notifications.
   * @service ToastrService
   * @param {BusinessEntityService} businessEntityService - The service for managing business entities.
   * @service BusinessEntityService
   * @param {FarmSiteService} farmsiteService - The service for managing farm sites.
   * @service FarmSiteService
   * @param {PersonsService} personService - The service for managing persons or individuals.
   * @service PersonsService
   * @param {CustomerLeadService} customeLeadService - The service for managing customer leads.
   * @service CustomerLeadService
   * @param {BusinessTypeService} businessTypeService - The service for managing business types.
   * @service BusinessTypeService
   * @param {BusinessCategoryService} businessCategoryService - The service for managing business categories.
   * @service BusinessCategoryService
   * @param {PersonRoleService} personRoleService - The service for managing person roles or positions.
   * @service PersonRoleService
   * @param {FormBuilder} formBuilder - The form builder for creating and managing forms.
   * @service FormBuilder
   * @param {ActivatedRoute} activatedRoute - The activated route for the current route information.
   * @service ActivatedRoute
   * @param {Router} router - The router for navigation within the application.
   * @service Router
   * @param {Sidenavaction} sidenavAction - The service for interacting with side navigation.
   * @service Sidenavaction
   * @param {CommonMethodsService} commonMethodsService - Service for common utility methods.
   * @service CommonMethodsService
   * @param {LocationStrategy} locationStrategy - Service for interacting with the browser's URL.
   * @service LocationStrategy
   * @param {PreviousRouteService} previousRouteService - Service for tracking navigation history.
   * @service PreviousRouteService
   * @param {CountriesAPIService} countriesAPIService - Service for fetching country data.
   * @service CountriesAPIService
   * @param {StatesAPIService} statesAPIService - Service for fetching state and village data.
   * @service StatesAPIService
   * @param {NgZone} ngZone - Service for executing work inside or outside of the Angular zone.
   * @service NgZone
   * @param {ChangeDetectorRef} cdr - Service for change detection.
   * @service ChangeDetectorRef
   */
  constructor(private toasterService: ToastrService, public businessEntityService: BusinessEntityService, public farmsiteService: FarmSiteService, public personService: PersonsService, public customeLeadService: CustomerLeadService, public businessTypeService: BusinessTypeService, public businessCategoryService: BusinessCategoryService, public personRoleService: PersonRoleService, public formBuilder: FormBuilder, public activatedRoute: ActivatedRoute, public router: Router, public sidenavAction: Sidenavaction, public commonMethodsService: CommonMethodsService,
    private locationStrategy: LocationStrategy, private previousRouteService: PreviousRouteService, public countriesAPIService: CountriesAPIService, public statesAPIService: StatesAPIService, private ngZone: NgZone, private cdr: ChangeDetectorRef
  ) {
    /**
     * Initializing a new Farm Site, Business Entity, Persons, Customer Lead, Business Type, Business Category, Person Role, Address and IAddress model.
     * This will be used to store the data for the current Farm Site, Business Entity, Persons, Customer Lead, Business Type, Business Category, Person Role, Address and IAddress data.
     */
    this.businessEntity = new BusinessEntity();
    this.farmsite = new FarmSiteModel();
    this.person = new PersonsModel();
    this.customerLead = new CustomerLeadModel();
    this.businessType = new BusinessType();
    this.businessCategory = new BusinessCategory();
    this.personRole = new PersonRoleModel();
    this.address = new Address();
    this.farmSiteAddress = new IAddress();
    /**
     * Initializing Quick Registration form for validation.
     * This FormGroup represents the form controls for the Quick Registration data.
     * @type {FormGroup}
     * @type {FormControl}
     */
    this.registerForm = this.formBuilder.group({
      businessType: ['', Validators.required],
      businessName: ['', [Validators.required,
        Validators.minLength(5),
        Validators.maxLength(30),
        Validators.pattern('^[a-zA-Z ]+$'),
      ]],
      businessCode: [null],
      suggestionName: [null],
      personName: ['', [Validators.required,
        Validators.minLength(3),
        Validators.maxLength(30),
        Validators.pattern('^[a-zA-Z ]*$')
      ]],
      email: ['', [Validators.pattern('[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-z]{2,6}$')
      ]],
      dialCode: ['',  [Validators.required]],
      mobileNumber: ['', Validators.required],
      countryName: ['', [Validators.required]],
      stateName: ['', [Validators.required]],
      villageName: ['', [Validators.required]],
    });
  }

  /**
   * Angular lifecycle hook - Component initialization
   *
   * Performs the following initialization steps:
   * 1. Tracks the current route.
   * 2. Resets component state and models.
   * 3. Loads reference data (Countries, Business Types, Categories, Roles).
   * 4. Sets default country to India and loads states.
   *
   * @async
   * @method ngOnInit
   * @returns {Promise<void>} A promise that resolves when the component initialization is completed.
   */
  public async ngOnInit(): Promise<void> {
    this.previousRouteService.setPreviousRoute(this.locationStrategy.path());
    this.stateVillageDisabled = false;
    this.businessEntity = new BusinessEntity();
    this.farmsite = new FarmSiteModel();
    this.person = new PersonsModel();
    this.customerLead = new CustomerLeadModel();
    this.address = new Address();
    this.farmSiteAddress = new IAddress();
    this.businessType = new BusinessType();
    this.businessCategory = new BusinessCategory();
    this.addUserToAcl = new addUserToAclObj();
    this.sendPwdTouser = new sendPasswordToMobileEmailObj();
    this.businessType.name = undefined;
    this.submitted = false;
    this.registraionSubmit = false;
    this.submitButtonDisabled = false;
    this.sidenavAction.x_navigation();
    this.countrySearch = undefined;
    this.stateSearch = undefined;
    this.villageSearch = undefined;
    this.registerForm.get('countryName').patchValue('');
    this.registerForm.get('stateName').patchValue('');
    this.registerForm.get('villageName').patchValue('');
    try {
       // Promise All
      this.countries = (await this.countriesAPIService.getAllCountriesList()).filter((country) => ['ES', 'ID', 'VN', 'NG', 'MY', 'AU', 'EC', 'SA', 'IN'].includes(country.countryCode));
      if (this.countries) {
        this.registerForm.get('countryName')!.setValue('India');
        this.address.country = 'India';
        const indianCountryIndexPosition = this.countries.findIndex((country) => country.name == 'India');
        if (indianCountryIndexPosition != -1) {
          const objectToMove = this.countries.splice(indianCountryIndexPosition, 1);
          this.countries.unshift(objectToMove[0]);
          this.filteredCountriesList = this.countries;
          this.getDialCode();
          this.getStates();
        }
      }
      this.businessTypes = await this.businessTypeService.getAllBusinessTypes();
      this.businessTypes = this.businessTypes.filter(businessType => businessType.name == 'Farm' || businessType.name == 'Feed Provider');
      this.businessCategories = await this.businessCategoryService.getAllBusinessCategories();
      this.personRoles = await this.personRoleService.getAllPersonRoles();
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * Retrieves and sets the dialing code based on the selected country.
   * Updates mobile number validation rules based on the country.
   *
   * @method getDialCode
   * @returns {void}
   */
  public getDialCode() {
    let item = this.countries.find(code => code.name == this.address.country);
    this.businessEntity.dialingCode = item.dialingCode[0];
    this.registerForm.get('mobileNumber').clearValidators();
    switch (item.name) {
      case 'Spain':
        this.registerForm.get('mobileNumber').setValidators([Validators.pattern('^[0-9]{9,9}$'), Validators.required]);
        this.registerForm.get('mobileNumber').updateValueAndValidity();
        break;
      case 'Indonesia':
        this.registerForm.get('mobileNumber').setValidators([Validators.pattern('^[0-9]{5,10}$'), Validators.required]);
        this.registerForm.get('mobileNumber').updateValueAndValidity();
        break;
      case 'Nigeria':
      case 'Vietnam':
        this.registerForm.get('mobileNumber').setValidators([Validators.pattern('^[0-9]{7,10}$'), Validators.required]);
        this.registerForm.get('mobileNumber').updateValueAndValidity();
        break;
      case 'Malaysia':
        this.registerForm.get('mobileNumber').setValidators([Validators.pattern('^[0-9]{7,9}$'), Validators.required]);
        this.registerForm.get('mobileNumber').updateValueAndValidity();
        break;
      case 'Australia':
        this.registerForm.get('mobileNumber').setValidators([Validators.pattern('^[0-9]{5,15}$'), Validators.required]);
        this.registerForm.get('mobileNumber').updateValueAndValidity();
        break;
      case 'Ecuador':
        this.registerForm.get('mobileNumber').setValidators([Validators.pattern('^[0-9]{8,8}$'), Validators.required]);
        this.registerForm.get('mobileNumber').updateValueAndValidity();
        break;
      case 'Saudi Arabia':
        this.registerForm.get('mobileNumber').setValidators([Validators.pattern('^[0-9]{8,9}$'), Validators.required]);
        this.registerForm.get('mobileNumber').updateValueAndValidity();
        break;
      default:
        this.registerForm.get('mobileNumber').setValidators([Validators.pattern('^[0-9]{10,10}$'), Validators.required]);
        this.registerForm.get('mobileNumber').updateValueAndValidity();
        break;
    }
  }

  /**
   * Getter for easy access to form controls in the template.
   *
   * @method f
   * @returns {AbstractControl} Form controls
   */
  public get f() {
    return this.registerForm.controls;
  }

  /**
   * Resets the form and component state to default values (Cancel action).
   */
  public cancel(): void {
    this.stateVillageDisabled = false;
    this.businessEntity = new BusinessEntity();
    this.farmsite = new FarmSiteModel();
    this.person = new PersonsModel();
    this.customerLead = new CustomerLeadModel();
    this.address = new Address();
    this.farmSiteAddress = new IAddress();
    this.businessType = new BusinessType();
    this.businessCategory = new BusinessCategory();
    this.addUserToAcl = new addUserToAclObj();
    this.sendPwdTouser = new sendPasswordToMobileEmailObj();
    this.businessType.name = undefined;
    this.submitted = false;
    this.registraionSubmit = false;
    this.isCountryDropdown = false;
    this.isStateDropdown = false;
    this.isVillageDropdown = false;
    this.countrySearch = 'India';
    this.address.country = 'India';
    this.submitButtonDisabled = false;
    this.getDialCode();
    this.stateSearch = 'Enter State';
    this.villageSearch = 'Enter Village';
    this.registerForm.get('countryName')!.setValue('India');
    this.registerForm.get('stateName').patchValue('');
    this.registerForm.get('villageName').patchValue('');
    this.ngOnInit();
    this.submitted = false;
    this.registerForm.reset();
  }

  /* Country Selection Field Start */
  /**
   * Filters country options based on search text.
   *
   * @method filterCountries
   * @returns {void}
   */
  public filterCountries(): void {
    const value = (this.registerForm.get('countryName')!.value || '') as CountriesModel | string;
    const searchText =
      typeof value === 'string' ? value.toLowerCase() : value.name.toLowerCase();

    this.filteredCountriesList = this.countries.filter(o =>
      o.name.toLowerCase().includes(searchText)
    );
  }

  /**
   * Handles country selection from dropdown.
   *
   * @method coutrySelection
   * @param {any} country - The selected country object.
   * @returns {void}
   */
  public coutrySelection(country: any): void {
    this.registerForm.get('countryName')!.setValue(country.name);
    this.isCountryDropdown = false;
    this.address.country = country.name;
    this.getDialCode();
    this.getStates();
  }

  /* Country Selection Field end */

  /* State Selection Field Start */
  /**
   * Filters state options based on search text.
   *
   * @method filterStates
   * @returns {void}
   */
  public filterStates(): void {
    const value = (this.registerForm.get('stateName')!.value || '') as StateModel | string;
    const searchText =
      typeof value === 'string' ? value.toLowerCase() : value.name.toLowerCase();

    this.filteredStatesList = this.states.filter(o =>
      o.name.toLowerCase().includes(searchText)
    );
  }

  /**
   * Handles state selection from dropdown.
   *
   * @method stateSelection
   * @param {any} state - The selected state object.
   * @returns {void}
   */
  public stateSelection(state: any): void {
    this.registerForm.get('stateName')!.setValue(state.name);
    this.farmSiteAddress.state = state.name;
    this.isStateDropdown = false;
    this.getVillages(state);
  }

  /* State Selection Field end */

  /**
   * Retrieves and populates the list of states based on the selected country.
   *
   * @method getStates
   * @returns {void}
   */
  public getStates(): void {
    this.states = [];
    this.villages = [];
    this.filteredStatesList = [];
    this.filteredVillagesList = [];
    const selectedCountryObject = this.countries.find((country: CountriesModel) => country.name == this.address.country);
    this.states = selectedCountryObject.states;
    this.filteredStatesList = this.states;
    this.stateVillageDisabled = false;
    this.registerForm.get('stateName')!.setValue('');
    this.registerForm.get('villageName')!.setValue('');
  }

  /* Village Selection Field Start */

  /**
   * Flag indicating if villages are currently loading.
   * @type {boolean}
   * @default false
   */
  public isLoadingVillages = false;

  /**
   * Flag indicating if more villages are being loaded (infinite scroll).
   * @type {boolean}
   * @default false
   */
  public isLoadingMore = false;

  /**
   * Flag indicating if there was an error loading villages.
   * @type {boolean}
   * @default false
   */
  public villagesLoadError = false;

  /**
   * Array of currently visible villages in the dropdown.
   * @type {any[]}
   * @default []
   */
  public visibleVillages: any[] = [];

  /**
   * Number of items to load per chunk.
   * @type {number}
   * @default 50
   */
  public CHUNK_SIZE = 50;

  /**
   * Current index for pagination.
   * @type {number}
   * @default 0
   */
  private currentIndex = 0;

  /**
   * Search text for filtering villages.
   * @type {string}
   * @default ''
   */
  private searchText = '';

  private isLoadTriggered = false;

  /**
   * Flag indicating if no data was found for the search.
   * @type {boolean}
   * @default false
   */
  public noDataFound = false;

  /**
   * Retrieves and populates the list of villages based on the selected state.
   *
   * @async
   * @method getVillages
   * @param {any} state - The selected state object.
   * @returns {Promise<void>}
   */
  public async getVillages(state: any) {
    this.resetVillages();
    this.isLoadingVillages = true;
    this.villagesLoadError = false;
    this.noDataFound = false;

    try {
      const response = await this.statesAPIService.getVillagesByStates(state.name);
      if (response?.[0]?.city_town_village?.length) {
        this.villages = response[0].city_town_village;
        this.loadNextChunk();
      } else {
        this.villagesLoadError = true;
      }
    } catch (err) {
      this.villagesLoadError = true;
    } finally {
      this.isLoadingVillages = false;
    }
  }

  /**
   * Loads the next chunk of villages for infinite scrolling.
   *
   * @private
   * @method loadNextChunk
   */
  private loadNextChunk() {
    const source = this.searchText
      ? this.villages.filter(v =>
          v['city_town_village_name'].toLowerCase().includes(this.searchText.toLowerCase())
        )
      : this.villages;

    if (source.length === 0) {
      this.noDataFound = true;
      return;
    }

    this.noDataFound = false; // reset when data exists
    const nextSet = source.slice(this.currentIndex, this.currentIndex + this.CHUNK_SIZE);
    this.visibleVillages = [...this.visibleVillages, ...nextSet];
    this.currentIndex += this.CHUNK_SIZE;
  }

  /**
   * Handles scroll event on the village dropdown to trigger loading more items.
   *
   * @method onVillageScroll
   * @returns {void}
   */
  public onVillageScroll() {
    if (this.isLoadTriggered || this.isLoadingMore) return;

    const remaining = this.getFilteredList()?.length - this.visibleVillages.length;
    if (remaining > 0) {
      this.isLoadTriggered = true;
      this.isLoadingMore = true;

      setTimeout(() => {
        this.ngZone.run(() => {
          this.loadNextChunk();
          this.isLoadingMore = false;
          this.isLoadTriggered = false;
          this.cdr.detectChanges();
        });
      }, 300);
    }
  }

  /**
   * Filters villages based on user input.
   *
   * @method filterVillages
   * @param {any} event - The input event.
   * @returns {void}
   */
  public filterVillages(event: any) {
    this.searchText = event.target.value;
    this.visibleVillages = [];
    this.currentIndex = 0;
    this.loadNextChunk();
  }

  /**
   * Helper to get the filtered list of villages.
   *
   * @private
   * @method getFilteredList
   */
  private getFilteredList() {
    return this.searchText
      ? this.villages.filter(v =>
          v['city_town_village_name'].toLowerCase().includes(this.searchText.toLowerCase())
        )
      : this.villages;
  }

  /**
   * Resets village data and state.
   *
   * @private
   * @method resetVillages
   */
  private resetVillages() {
    this.villages = [];
    this.visibleVillages = [];
    this.currentIndex = 0;
    this.searchText = '';
    this.isLoadTriggered = false;
    this.noDataFound = false;
    this.registerForm.get('villageName')?.setValue('');
  }

  /**
   * Handles focus event on the village input.
   *
   * @method onVillageFocus
   * @returns {void}
   */
  public onVillageFocus() {
    if (!this.searchText) {
      // If no search text, just reload first chunk
      this.visibleVillages = [];
      this.currentIndex = 0;
      this.loadNextChunk();
    } else {
      // If search exists, reload search result
      this.visibleVillages = [];
      this.currentIndex = 0;
      this.loadNextChunk();
    }
  }

  /**
   * Handles blur event on the village input.
   *
   * @method onVillageBlur
   * @returns {void}
   */
  public onVillageBlur() {
    if (this.noDataFound || this.visibleVillages.length == 0) {
      this.clearVillageField();
    }
  }

  /**
   * Clears the village input field.
   *
   * @private
   * @method clearVillageField
   */
  private clearVillageField() {
    this.registerForm.get('villageName')?.setValue('');
    this.visibleVillages = [];
    this.currentIndex = 0;
    this.searchText = '';
  }

  /**
   * Handles village selection from dropdown.
   *
   * @method villageSelection
   * @param {any} village - The selected village object.
   * @returns {void}
   */
  public villageSelection(village: any): void {
    this.registerForm.get('villageName')!.setValue(village.city_town_village_name);
    this.isVillageDropdown = false;
    this.villageSearch = village.city_town_village_name;
    this.farmSiteAddress.village = village.city_town_village_name;
    if (this.farmSiteAddress.village) {
      this.checkPincode(this.farmSiteAddress.village, this.farmSiteAddress.state, village.city_town_village_name.toLowerCase().includes(',') ? village.city_town_village_name.split(',')[1] : '', village.city_town_village_name.toLowerCase().includes(',') ? village.city_town_village_name.split(',')[2] : '');
    }
  }
  /* Village Selection Field end */

  /**
   * Checks the latitude and longitude data for a given location and updates the address object.
   *
   * @async
   * @method checkPincode
   * @param {string} village - The name of the village.
   * @param {string} state - The name of the state.
   * @param {string} mandal - The name of the mandal.
   * @param {string} district - The name of the district.
   * @returns {Promise<void>} A promise that resolves when the latitude and longitude data is updated.
   */
  public async checkPincode(village: string, state: string, mandal: string, district: string): Promise<void> {
    try {
      let latLongs = await this.farmsiteService.latLongsData(village, state, mandal, district);
      if (latLongs.results.length > 0) {
        this.farmSiteAddress.latitude = latLongs.results[0].geometry.location.lat;
        this.farmSiteAddress.longitude = latLongs.results[0].geometry.location.lng;
      }
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * Fetches business code suggestions based on the business name.
   *
   * @async
   * @method getBusinessCode
   */
  public async getBusinessCode() {
    if (this.businessEntity.businessName != '' && this.businessEntity.businessName != undefined) {
      try {
        const businessCodes = await this.businessEntityService.getBusinessCodeSuggestions(this.processFarmName(this.businessEntity.businessName), 'noCode');
        if (businessCodes && businessCodes['suggestions'].length > 0) {
          this.businessEntity.businessCode = businessCodes['suggestions'][0];
        }
      } catch (error) {

      }
    }
  }

  /**
   * Handles changes to the business name input.
   *
   * @method onBusinessChange
   * @returns {void}
   */
  public onBusinessChange() {
    if (this.businessEntity.businessName == '' || this.businessEntity.businessName == undefined) {
      this.businessEntity.businessCode = '';
    }
  }

  /**
   * Processes the farm name to format it (CamelCase).
   *
   * @method processFarmName
   * @param {string} farmName - The raw farm name.
   * @returns {string} The formatted farm name.
   */
  public processFarmName(farmName: string) {
    let trimmedFarmName = farmName.trim();
    if (trimmedFarmName.includes(' ')) {
      this.finalFarmName = trimmedFarmName
        .toLowerCase()
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join('');
      return this.finalFarmName;
    } else {
      return trimmedFarmName;
    }
  }

  /**
   * Initiates the registration process.
   * Creates a customer lead first, then proceeds to create the business entity.
   *
   * @async
   * @method Registration
   * @returns {Promise<void>} A Promise that resolves when the process is completed.
   * @throws {Error} If there's an error during registration.
   * @side-effect Creates CustomerLead, calls addBusiness.
   */
  public async Registration(): Promise<void> {
    this.submitted = true;
    this.registerForm.markAllAsTouched();
    // stop here if form is invalid
    if (this.registerForm.invalid) {
      return;
    }
    this.submitButtonDisabled = true;
    let businessType = this.businessTypes.find(businesstype => businesstype.name == this.businessType.name);
    let customerLeads = await this.customeLeadService.getCustomerLeadsByMobileNumber(this.businessEntity.mobileNumber);
    if (customerLeads && customerLeads.length > 0) {
      this.toasterService.warning('Customer Lead already exists with that mobile number', '', {
        timeOut: 2000
      });
      this.submitButtonDisabled = false;
      return;
    } else {
      try {
        this.registraionSubmit = true;
        const countryItem = this.countries.find(countryName => countryName && countryName.name == this.address.country);
        this.customerLead.countryCode = countryItem.isoCode;
        this.customerLead.lead_culture = {
          id: 1,
          name: 'Shrimp'
        };
        this.customerLead.mobileNumber = this.businessEntity.mobileNumber;
        this.customerLead.dialingCode = this.businessEntity.dialingCode;
        this.customerLead.lead_from = 'Farmer';
        this.customerLead.event = 'Register as farmer';
        this.customerLead.lead_widget = 'bb_web';
        this.customerLead.isActive = true;
        this.customerLead.status = 'Archived';
        this.customerLead.converted = true;
        this.customerLead.lead_reference = 'customerSupport';
        this.customerLead.businessType = [{
          id: businessType.id,
          name: businessType.name
        }];
        this.customerLead.status_date = (moment(new Date()).format('YYYY-MM-DD')).toString();
        this.customerLead.generatedDate = (moment(new Date()).format('YYYY-MM-DD')).toString();
        this.customerLead = await this.customeLeadService.create(this.customerLead);
        if (this.customerLead) {
          this.addBusiness();
        } else {
          this.toasterService.error('Business is not registered, Please try again!');
          this.ngOnInit();
        }
      } catch (error) {
        this.commonMethodsService.userFriendlyAlertMessages(error);
        this.registraionSubmit = false;
        this.submitButtonDisabled = false;
      }
    }
  }

  /**
   * Creates the business entity linked to the customer lead.
   *
   * @async
   * @method addBusiness
   * @returns {Promise<void>} A Promise that resolves when the process is completed.
   * @throws {Error} If there's an error during business creation.
   * @side-effect Creates BusinessEntity, calls addFarmsite.
   */
  public async addBusiness(): Promise<void> {
    try {
      if (this.businessEntity.email) {
        this.businessEntity.email = this.businessEntity.email.toLowerCase();
      }
      const item = this.countries.find(countryName => countryName && countryName.name === this.address.country);
      if (!item) {
        throw new Error('Country not found');
      }
       this.businessEntity.address = [{
        country: item.name,
        houseNo: '',
        street: '',
        landMark: this.farmSiteAddress.village.toLowerCase().includes(',') ? this.farmSiteAddress.village.split(',')[1] : this.farmSiteAddress.village,
        state: this.farmSiteAddress.state,
        district: this.farmSiteAddress.village.toLowerCase().includes(',') && (this.farmSiteAddress.village.match(/,/g)).length == 2 ? this.farmSiteAddress.village.split(',')[2] : (this.farmSiteAddress.village.toLowerCase().includes(',') && (this.farmSiteAddress.village.match(/,/g)).length == 1 ? this.farmSiteAddress.village.split(',')[1] : this.farmSiteAddress.state),
        city: this.farmSiteAddress.village.toLowerCase().includes(',') ? this.farmSiteAddress.village.split(',')[0] : this.farmSiteAddress.village,
        pincode: '',
        countryCode: item.isoCode
       }];
      const type = this.businessTypes.find(btype => btype && btype.name === this.businessType.name);
      if (!type) {
        throw new Error('Business type not found');
      }
      this.businessType.businessTypeID = type.id;
      this.businessCategories = await this.businessCategoryService.getAllBusinessCategories();
      let result = this.businessCategories.filter(obj => {
        return obj.name === (this.businessType.name === 'Farm' ? 'Farm' : 'Provider');
      });
      if (result.length > 0) {
        this.businessEntity.businessCategory = [{ name: result[0].name, businessCategoryID: result[0].id }];
      }
      this.submitButtonDisabled = true;
      this.businessEntity.businessType.push(this.businessType);
      this.businessEntity = await this.businessEntityService.create(this.businessEntity);
      if (this.businessEntity) {
        this.businessTypeName = this.businessEntity.businessType[0].name;
        this.businessID = this.businessEntity.id;
        this.addFarmsite();
      } else {
        const customerleaddelete = await this.customeLeadService.deleteOneByID(this.customerLead.id);
        if (customerleaddelete) {
          this.toasterService.error('Business is not Registered, Please try again!');
          this.submitButtonDisabled = false;
          this.ngOnInit();
        }
      }
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
      this.registraionSubmit = false;
    }
  }

  /**
   * Alternative method for adding business (appears unused or legacy).
   *
   * @async
   * @method addBusiness1
   */
  public async addBusiness1(): Promise<void> {
    try {
      if (this.businessEntity.email) {
        this.businessEntity.email = this.businessEntity.email.toLowerCase();
      }
      const item = this.countries.find(countryName => countryName && countryName.name == this.address.country);
      this.businessEntity.address = [{
        country: item.name,
        houseNo: '',
        street: '',
        landMark: '',
        state: this.farmSiteAddress.state,
        district: this.farmSiteAddress.village.toLowerCase().includes(',') && (this.farmSiteAddress.village.match(/,/g)).length == 2 ? this.farmSiteAddress.village.split(',')[2] : (this.farmSiteAddress.village.toLowerCase().includes(',') && (this.farmSiteAddress.village.match(/,/g)).length == 1 ? this.farmSiteAddress.village.split(',')[1] : this.farmSiteAddress.state),
        city: this.farmSiteAddress.village.toLowerCase().includes(',') ? this.farmSiteAddress.village.split(',')[0] : this.farmSiteAddress.village,
        pincode: '',
        countryCode: item.countryCode
      }];
      const type = this.businessTypes.find(btype => btype && btype.name == this.businessType.name);
      this.businessType.businessTypeID = type.id;
      if (this.businessType.name == 'Farm') {
        this.businessCategories = await this.businessCategoryService.getAllBusinessCategories();
        let result = this.businessCategories.filter(obj => {
          return obj.name === 'Farm';
        });
        this.businessEntity.businessCategory = [{name: result && result[0].name, businessCategoryID: result && result[0].id}];
      } else {
        this.businessCategories = await this.businessCategoryService.getAllBusinessCategories();
        let result = this.businessCategories.filter(obj => {
          return obj.name === 'Provider';
        });
        this.businessEntity.businessCategory = [{name: result && result[0].name, businessCategoryID: result && result[0].id}];
      }
      this.submitButtonDisabled = true;
      this.businessEntity.businessType.push(this.businessType);
      return
      this.businessEntity = await this.businessEntityService.create(this.businessEntity);
      if (this.businessEntity) {
        this.businessTypeName = this.businessEntity.businessType[0].name;
        this.businessID = this.businessEntity.id;
        this.addFarmsite();
      } else {
        let customerleaddelete = await this.customeLeadService.deleteOneByID(this.customerLead.id);
        if (customerleaddelete) {
          this.toasterService.error('Business is not Registered, Please try again!');
          this.submitButtonDisabled = false;
          this.ngOnInit();
        }
      }
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
      this.registraionSubmit = false;
    }
  }

  /**
   * Creates a farm site (location) for the business.
   *
   * @async
   * @method addFarmsite
   * @returns {Promise<void>} A Promise that resolves when the process is completed.
   * @throws {Error} If there's an error during farm site creation.
   * @side-effect Creates FarmSite, calls addPerson.
   */
  public async addFarmsite(): Promise<void> {
    try {
      this.farmsite.businessID = this.businessEntity.id;
      this.farmsite.farmNameAtLocation = this.address.country == 'India' ? this.farmSiteAddress.village.split(',').slice(0, 2).join(',') + ', ' + this.farmSiteAddress.state : this.farmSiteAddress.village;
      this.farmsite.contract = null;
      this.farmsite.waterType = null;
      this.farmsite.soilType = null;
      this.farmsite.buyerUserConfig = '';
      this.farmsite.chatUserConfig = '';
      this.farmsite.SMEContract = null;
      this.farmsite.buyerContract = null;
      this.farmsite.address = {
        village: this.farmSiteAddress.village.toLowerCase().includes(',') ? this.farmSiteAddress.village.split(',')[0] : this.farmSiteAddress.village,
        taluk: this.farmSiteAddress.village.toLowerCase().includes(',') && (this.farmSiteAddress.village.match(/,/g)).length == 2 ? this.farmSiteAddress.village.split(',')[1] : (this.farmSiteAddress.village.toLowerCase().includes(',') && (this.farmSiteAddress.village.match(/,/g)).length == 1 ? this.farmSiteAddress.village.split(',')[0] : this.farmSiteAddress.state),
        state: this.farmSiteAddress.state,
        pincode: this.farmSiteAddress.pincode,
        mandal: this.farmSiteAddress.village.toLowerCase().includes(',') && (this.farmSiteAddress.village.match(/,/g)).length == 2 ? this.farmSiteAddress.village.split(',')[1] : (this.farmSiteAddress.village.toLowerCase().includes(',') && (this.farmSiteAddress.village.match(/,/g)).length == 1 ? this.farmSiteAddress.village.split(',')[0] : this.farmSiteAddress.state),
        district: this.farmSiteAddress.village.toLowerCase().includes(',') && (this.farmSiteAddress.village.match(/,/g)).length == 2 ? this.farmSiteAddress.village.split(',')[2] : (this.farmSiteAddress.village.toLowerCase().includes(',') && (this.farmSiteAddress.village.match(/,/g)).length == 1 ? this.farmSiteAddress.village.split(',')[1] : this.farmSiteAddress.state),
        latitude: this.farmSiteAddress.latitude,
        longitude: this.farmSiteAddress.longitude,
        country: this.address.country
      };
      this.submitButtonDisabled = true;
      this.farmsite = await this.farmsiteService.create(this.farmsite);
      if (this.farmsite) {
        this.addPerson();
      } else {
        let customerleaddelete = await this.customeLeadService.deleteOneByID(this.customerLead.id);
        let businessdelete = await this.businessEntityService.deleteOneByID(this.businessEntity.id);
        if (customerleaddelete && businessdelete) {
          this.toasterService.error('Business is not Registered, Please try again!');
          this.submitButtonDisabled = false;
          this.ngOnInit();
        }
      }
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
      this.registraionSubmit = false;
      this.submitButtonDisabled = false;
    }
  }

  /**
   * Creates a person (user) associated with the business and farm site.
   * Generates a password and initiates verification.
   *
   * @async
   * @method addPerson
   * @returns {Promise<void>} A Promise that resolves when the process is completed.
   * @side-effect Creates Person, calls personVerification.
   */
  public async addPerson(): Promise<void> {
    try {
      if (this.businessTypeName == 'Farm' || this.businessTypeName == 'Feed Provider') {
        this.personType = 'Business Admin';
      }
      if (this.businessID) {
        this.personRoles = await this.personRoleService.getAllPersonRoles();
        if (this.personType) {
          const type = this.personRoles.find(role => role && role.name == this.personType);
          this.person.personType = [
            {
              personRoleID: type.id,
              name: this.personType
            }
          ];
        }
        this.person.businessID = this.businessID;
        this.person.mobileNumber = this.businessEntity.mobileNumber;
        this.person.email = this.businessEntity.email;
        this.dialingCode = this.businessEntity.dialingCode;
        let item = this.countries.find(code => code.name == this.address.country);
        this.person.dialingCode = item.dialingCode[0];
        this.person.address = [{
          country: item.name,
          houseNo: '',
          street: '',
          landMark: '',
          state: this.farmSiteAddress.state,
          district: this.farmSiteAddress.village.toLowerCase().includes(',') && (this.farmSiteAddress.village.match(/,/g)).length == 2 ? this.farmSiteAddress.village.split(',')[2] : (this.farmSiteAddress.village.toLowerCase().includes(',') && (this.farmSiteAddress.village.match(/,/g)).length == 1 ? this.farmSiteAddress.village.split(',')[1] : this.farmSiteAddress.state),
          city: this.farmSiteAddress.village.toLowerCase().includes(',') ? this.farmSiteAddress.village.split(',')[0] : this.farmSiteAddress.village,
          pincode: '',
        }];
        this.person.reporting = 'self';
        this.submitButtonDisabled = true;
        this.person = await this.personService.addPerson(this.person);
        if (this.person) {
          this.passowrdGenerated = await this.personService.passowrdGeneration();
          if (this.passowrdGenerated.password) {
            this.personVerification();
          } else {
            this.passowrdGenerated = await this.personService.passowrdGeneration();
            if (this.passowrdGenerated) {
              this.personVerification();
            }
          }
        }
      }
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
      this.registraionSubmit = false;
      this.submitButtonDisabled = false;
    }
  }

  /**
   * Verifies the person by adding them to the ACL and sending credentials.
   *
   * @async
   * @method personVerification
   * @returns {Promise<void>} A Promise that resolves when the verification process is completed.
   * @side-effect Adds user to ACL, sends email/SMS, shows success message.
   */
  public async personVerification(): Promise<void> {
    try {
      this.addUserToAcl = {
        userName: this.person.mobileNumber,
        password: this.passowrdGenerated.password,
        role: this.person.personType[0].name,
        businessID: this.person.businessID,
        personID: this.person.id
      };
      this.addUsertoacl = await this.personService.addUserToAcl(this.addUserToAcl);
      if (this.addUsertoacl != 'User not created') {
        this.sendPwdTouser = {
          mobileNumber: this.person.mobileNumber,
          dialingCode: String(this.dialingCode),
          email: this.person.email,
          password: this.addUserToAcl.password,
          businessID: this.person.businessID,
          templateNameCode: 'WELCOME_BLUBAY'
        };
        this.passowrdSent = await this.personService.sendUserPwdToMobileEmail(this.sendPwdTouser);
        this.submitButtonDisabled = false;
        if (this.passowrdSent) {
          this.toasterService.success('Registration successfull', '', {
            timeOut: 2000
          });
          this.submitted = false;
          this.registerForm.reset(); //  Reset form values
          this.submitted = false;    // Reset validation flag
          this.registerForm.markAsPristine(); // optional - clears dirty flags
          this.registerForm.markAsUntouched(); // optional - clears touched state
          this.ngOnInit();
        }
      } else {
        let leadDelete = await this.customeLeadService.deleteOneByID(this.customerLead.id);
        let businessDelete = await this.businessEntityService.deleteOneByID(this.businessEntity.id);
        let farmsiteDelete = await this.farmsiteService.deleteOneByID(this.farmsite.id);
        let personDelete = await this.personService.deleteOneByID(this.person.id);
        if (leadDelete && businessDelete && farmsiteDelete && personDelete) {
          this.toasterService.error('Registraion is not Successful, Please try again!');
          this.ngOnInit();
        }
      }
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * Angular lifecycle hook - Component destruction
   *
   * @method ngOnDestroy
   * @returns {void}
   */
  public ngOnDestroy(): void {
    this.businessTypeService.cancelOnGoingHttpRequests();
    this.businessCategoryService.cancelOnGoingHttpRequests();
    this.customeLeadService.cancelOnGoingHttpRequests();
    this.personRoleService.cancelOnGoingHttpRequests();
  }
}
